import { PRESCRIPTION_TYPE, PT_PRESCRIPTION_TYPE, translatePrescriptionTypeToPt, StructuredPacking } from '../interfaces/medicine';
import { TipoAntimicrobianoEnum, TotalTreatmentEnum, SOLID_MEDICINE_UNITS } from '../contants/medicineEdition';
import { PosologyHistoricItem, PosologiaResponseType, PosologiaEstruturada } from '../interfaces/prescription';

export const filterPackings = (packings: string[]): string[] => {
  return packings.filter((value) => !!value).sort((a, b) => +a - +b);
};

/**
 * Tipo de item retornado pela busca para produtos correlatos (não medicamentos, ex.: sabonete, protetor solar).
 */
export const CORRELATO_MEDICINE_TYPE = 'CORRELATO';

/**
 * Indica se um item é um "produto correlato" (não medicamento).
 * Itens correlatos não exigem posologia e não disparam a estruturação/cálculo do tratamento.
 *
 * A detecção considera dois campos, pois nem todos os shapes de medicamento carregam ambos:
 * o `TipoMedicamento` vem da busca/edição, enquanto o `ProdutoCorrelato` está presente nos
 * itens da prescrição (lista, cards e validações de completude).
 *
 * @param medicine - Objeto do medicamento contendo `TipoMedicamento` e/ou `ProdutoCorrelato`.
 * @returns `true` quando o item é um produto correlato.
 */
export const isCorrelatoMedicine = (
  medicine?: { TipoMedicamento?: string; ProdutoCorrelato?: boolean } | null
): boolean => medicine?.TipoMedicamento === CORRELATO_MEDICINE_TYPE || medicine?.ProdutoCorrelato === true;

export const getMedicineTypeOutput = (
  isCompounded: boolean,
  medicineType: PRESCRIPTION_TYPE
): PT_PRESCRIPTION_TYPE => {
  if (medicineType === PRESCRIPTION_TYPE.OTHER) return PT_PRESCRIPTION_TYPE.OUTRO;

  const currentType = isCompounded ? (`COMPOUNDED_${medicineType}` as PRESCRIPTION_TYPE) : medicineType;

  const translated = translatePrescriptionTypeToPt(currentType);
  return translated || PT_PRESCRIPTION_TYPE.OUTRO;
};

export const getTreatmentWarningText = (
  maximumDaysOfTreatment: number | undefined,
  idTipoAntimicrobiano: number | undefined
): string => {
  if (idTipoAntimicrobiano === TipoAntimicrobianoEnum.GLP1) {
    return 'Conforme a IN 360/2025, a prescrição desse medicamento é válida por 90 dias a partir da data de emissão.';
  }

  if (maximumDaysOfTreatment) {
    return `Portaria 344/98: a quantidade prescrita deve corresponder a no máximo ${maximumDaysOfTreatment}
                dias de tratamento`;
  }

  return '';
};

export const formatTreatmentNumber = (value: number | string): string => {
  const formatter = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return formatter.format(Number(value));
};

export const formatQuantityValue = (value: number | string | null): string => {
  if (value === null || value === undefined) return '0';
  
  const numValue = Number(value);
  
  return isNaN(numValue) ? '0' : (numValue % 1 === 0 ? numValue.toString() : numValue.toFixed(2).replace(/\.?0+$/, ''));
};

export const normalizeUndefinedValue = (value: any): number | null => {
  if (value === 'indefinido' || value === TotalTreatmentEnum.INDEFINIDO) {
    return null;
  }
  
  const numericValue = Number(value);
  if (isNaN(numericValue)) {
    return null;
  }
  
  return numericValue;
};

export const isValidTreatmentValue = (value: any): boolean => {
  return typeof value === 'number' && !isNaN(value) && value > 0;
};

export const isTreatmentUndefined = (totalTreatment: any): boolean => {
  if (totalTreatment === 'indefinido' || totalTreatment === TotalTreatmentEnum.INDEFINIDO) {
    return true;
  }
  
  if (totalTreatment === null || totalTreatment === undefined) {
    return true;
  }
  
  if (totalTreatment === 0 || totalTreatment === '0') {
    return true;
  }
  
  return false;
};

export const isSolidMedicine = (pharmaceuticalForm?: string): boolean => {
  if (!pharmaceuticalForm) return false;

  const lowercasedForm = pharmaceuticalForm.toLocaleLowerCase();

  return SOLID_MEDICINE_UNITS.some((solidMedicineUnit) => lowercasedForm.includes(solidMedicineUnit));
};

export const shouldShowStructuredPosology = (
  isLoadingPosology: boolean,
  canAutomaticallySetPosology: boolean,
  isSMRForm: boolean,
  automaticallySetPosology: boolean,
  posology: any
): boolean => {
  return !isLoadingPosology && canAutomaticallySetPosology && !isSMRForm && automaticallySetPosology && !!posology;
};

export const shouldShowPackingAndUnitInputs = (
  isLoadingPosology: boolean,
  enableStructuredPosology: boolean,
  canShowPosology: boolean,
  automaticallySetPosology: boolean,
  posologyError?: string,
  posologyResponse?: PosologiaResponseType | null
): boolean => {
  if (isLoadingPosology) {
    return false;
  }

  if (posologyError) {
    return true;
  }

  if (!enableStructuredPosology) {
    return true;
  }

  // If user clicked "Edit", show inputs
  if (!automaticallySetPosology) {
    return true;
  }

  // If treatment is indefinido, show inputs alongside the banner
  if (posologyResponse && isTreatmentUndefined(posologyResponse.total_tratamento)) {
    return true;
  }

  // If structured posology is available, hide inputs
  if (canShowPosology) {
    return false;
  }
  
  return !enableStructuredPosology;
};

export const formatSolidTreatmentText = (totalTreatment: string, unit: string): string => {
  return `${formatTreatmentNumber(totalTreatment)} ${unit}`;
};

export const formatLiquidTreatmentText = (
  totalTreatment: string,
  unit: string,
  suggestedPackagingQuantity: number,
  packingType: string,
  suggestedPackaging: number
): string => {
  return `${formatTreatmentNumber(totalTreatment)} ${unit} | ${suggestedPackagingQuantity} ${packingType} de ${suggestedPackaging} ${unit}`;
};

export const isPosologyHistoricComplete = (item: PosologyHistoricItem): boolean => {
  return (
    item.totalTratamento !== null &&
    item.quantidade !== null &&
    item.quantidadeEmbalagemSugerida !== null &&
    item.unidade !== null &&
    item.unidadePlural !== null &&
    item.embalagemSugerida !== null
  );
};

export const findMatchingPacking = (
  currentPacking: StructuredPacking,
  availablePackings: StructuredPacking[]
): StructuredPacking | undefined => {
  if (!currentPacking) {
    return undefined;
  }

  if (!availablePackings || availablePackings.length === 0) {
    return undefined;
  }

  const exactMatch = availablePackings.find(
    (packing) =>
      packing.TipoEmbalagem === currentPacking.TipoEmbalagem &&
      packing.VolumeEmbalagem === currentPacking.VolumeEmbalagem
  );

  if (exactMatch) return exactMatch;

  const partialMatch = availablePackings.find(
    (packing) => packing.TipoEmbalagem === currentPacking.TipoEmbalagem
  );

  if (partialMatch) return partialMatch;

  return availablePackings[0];
};

export const transformPosologyToStructured = (
  posology: PosologiaResponseType | null | undefined
): PosologiaEstruturada | undefined => {
  if (!posology) {
    return undefined;
  }

  const requiredFields = [
    'total_tratamento',
    'quantidade',
    'unidade',
    'embalagem_sugerida',
    'quantidade_embalagem_sugerida'
  ] as const;

  const hasAllRequiredFields = requiredFields.every(
    field => posology[field] != null
  );

  if (!hasAllRequiredFields) {
    return undefined;
  }

  return {
    TotalTratamento: String(posology.total_tratamento),
    Quantidade: String(posology.quantidade),
    Unidade: posology.unidade,
    UnidadePlural: posology.unidade_plural || posology.unidade,
    EmbalagemSugerida: posology.embalagem_sugerida,
    QuantidadeEmbalagemSugerida: posology.quantidade_embalagem_sugerida,
  };
};
